import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Icon,
} from '@material-ui/core';

import Payment from '../../../services/Payment';
import { formatMoney } from '../../../utils/common';
import { XMEI_PRIMARY_COLOR } from '../../../consts/common';
import { useStyles as useBaseStyles } from '../baseStyle';

const useStyles = makeStyles((theme) => ({
  section: {
    width: '100%',
    maxWidth: 640,
  },
  card: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: '#6B7280',
    marginBottom: theme.spacing(2),
  },
  pixImage: {
    width: '100%',
    maxWidth: 280,
    height: 'auto',
    display: 'block',
    margin: '0 auto 16px auto',
  },
  pixCode: {
    wordBreak: 'break-all',
    backgroundColor: '#F5F5F5',
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: 12,
  },
  button: {
    marginTop: theme.spacing(2),
    backgroundColor: XMEI_PRIMARY_COLOR,
    color: '#FFF',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: XMEI_PRIMARY_COLOR,
    },
  },
  secondaryButton: {
    marginTop: theme.spacing(1),
  },
  successText: {
    color: 'green',
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: 'red',
    marginTop: theme.spacing(2),
  },
}));

const formatDueDate = (dueDate) => {
  if (!dueDate || !dueDate.includes('-')) return dueDate;

  const [year, month, day] = dueDate.split('-');
  return `${day}/${month}/${year}`;
};

const POLL_INTERVAL_MS = 5000;

function PagamentoComPix(props) {
  const baseClasses = useBaseStyles(props);
  const classes = useStyles();
  const history = useHistory();
  const usuarioLogado = useSelector((state) => state.user);

  const [assinatura, setAssinatura] = useState(null);
  const [message, setMessage] = useState({
    text: '',
    color: '',
    icon: '',
  });
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [fatura, setFatura] = useState(null);
  const [confirmado, setConfirmado] = useState(false);
  const pixRequestedRef = useRef(false);

  const state = props.location.state;
  const isPublicSignup = !!state?.user;
  const user = state?.user || usuarioLogado || {};
  const servico = state?.item || null;

  const valorAssinatura = assinatura ? Number(assinatura.valor) / 100 : 0;
  const valorServico = servico ? Number(servico.valor) : 0;
  const valorTotal = valorAssinatura + valorServico;

  useEffect(() => {
    if (state?.signaturePlan) {
      setAssinatura(state.signaturePlan);
    }
  }, [state]);

  useEffect(() => {
    if (!fatura?.id || confirmado) return undefined;

    const token = localStorage.getItem('toobiauth') || '';

    const checkStatus = async () => {
      try {
        const response = await Payment.verify_pix_signature({
          token,
          faturaId: fatura.id,
        });

        if (response?.data?.confirmado) {
          setConfirmado(true);
          setMessage({
            text: 'Pagamento confirmado! Sua assinatura está ativa.',
            color: 'green',
            icon: 'check_circle_outline',
          });
        }
      } catch (err) {
        // silencia erros temporários do polling
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fatura, confirmado]);

  const handleGeneratePix = async () => {
    const cpf_cnpj = user.cpf || user.cnpj;

    if (!assinatura || !cpf_cnpj) {
      setMessage({
        text: 'Não foi possível gerar o Pix. Verifique os dados do usuário.',
        color: 'red',
        icon: 'remove_circle_outline',
      });
      return;
    }

    setLoading(true);
    setMessage({ text: '', color: '', icon: '' });

    try {
      const token = localStorage.getItem('toobiauth') || '';
      const response = await Payment.signature_payment_pix({
        token,
        assinatura,
        email: user.email,
        cpf_cnpj,
        username: user.nome,
        usuario: isPublicSignup ? user : undefined,
        portal: isPublicSignup ? false : true,
        servico,
      });

      setPixData(response.data.pix);
      setFatura(response.data.fatura);
      setMessage({
        text: 'Pix gerado com sucesso. Faça o pagamento para ativar a assinatura.',
        color: 'green',
        icon: 'check_circle_outline',
      });
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || 'Não foi possível gerar o Pix.';

      setMessage({
        text: errorMessage,
        color: 'red',
        icon: 'remove_circle_outline',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pixRequestedRef.current) return;
    if (!assinatura) return;
    const cpf_cnpj = user.cpf || user.cnpj;
    if (!cpf_cnpj) return;

    pixRequestedRef.current = true;
    handleGeneratePix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, user?.cpf, user?.cnpj]);

  const handleCopyPix = async () => {
    if (!pixData?.qrcode_text || !navigator.clipboard) return;

    await navigator.clipboard.writeText(pixData.qrcode_text);
    setMessage({
      text: 'Código Pix copiado para a área de transferência.',
      color: 'green',
      icon: 'check_circle_outline',
    });
  };

  return (
    <Grid
      container
      spacing={0}
      alignItems="center"
      justifyContent="center"
      className={baseClasses.grid}
    >
      <Paper elevation={10} className={baseClasses.root}>
        <div className={baseClasses.logoContainer}>
          <img
            src="/logo-black.png"
            alt="Xmei Logo"
            className={baseClasses.logo}
          />
        </div>

        <div className={classes.section}>
          <Typography className={classes.title}>Pagamento via Pix</Typography>
          <Typography className={classes.subtitle}>
            Pague o Pix abaixo para ativar a assinatura.
          </Typography>

          <Card className={classes.card}>
            <CardContent>
              <Typography variant="subtitle2">Assinatura</Typography>
              <Typography variant="h6">
                {assinatura?.nome || 'Plano não informado'}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                {formatMoney(valorAssinatura)}
              </Typography>

              {servico && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                    Serviço
                  </Typography>
                  <Typography variant="h6">{servico.nome}</Typography>
                  <Typography variant="body2" style={{ marginTop: 4 }}>
                    {formatMoney(valorServico)}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                Valor total
              </Typography>
              <Typography variant="h6">{formatMoney(valorTotal)}</Typography>
            </CardContent>
          </Card>

          {!pixData && loading && (
            <Card className={classes.card}>
              <CardContent
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <CircularProgress size={24} />
                <Typography>Gerando Pix...</Typography>
              </CardContent>
            </Card>
          )}

          {!pixData && !loading && (
            <Button
              fullWidth
              variant="contained"
              className={classes.button}
              onClick={handleGeneratePix}
            >
              GERAR PIX
            </Button>
          )}

          {pixData && !confirmado && (
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="subtitle2">QR Code</Typography>
                {pixData.qrcode && (
                  <img
                    src={pixData.qrcode}
                    alt="QR Code Pix"
                    className={classes.pixImage}
                  />
                )}

                <Typography variant="subtitle2">Pix copia e cola</Typography>
                <Typography className={classes.pixCode}>
                  {pixData.qrcode_text}
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  className={classes.button}
                  onClick={handleCopyPix}
                >
                  COPIAR CÓDIGO PIX
                </Button>

                <Typography
                  variant="body2"
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <CircularProgress size={16} />
                  Aguardando confirmação do pagamento...
                </Typography>

                {fatura?.due_date && (
                  <Typography variant="body2" style={{ marginTop: 16 }}>
                    Vencimento: {formatDueDate(fatura.due_date)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {confirmado && (
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" className={classes.successText}>
                  <Icon className="text-20">check_circle_outline</Icon>{' '}
                  Pagamento confirmado!
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  Sua assinatura está ativa.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.button}
                  onClick={() => history.push('/login')}
                >
                  IR PARA LOGIN
                </Button>
              </CardContent>
            </Card>
          )}

          {message.text !== '' && (
            <Typography
              className={
                message.color === 'green'
                  ? classes.successText
                  : classes.errorText
              }
            >
              <Icon className="text-20">{message.icon}</Icon> {message.text}
            </Typography>
          )}
        </div>
      </Paper>
    </Grid>
  );
}

export default PagamentoComPix;
